import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'localization';
import {
  InputAtom,
  TextareaAtom,
  PrimeReactButton,
  SpinnerAtom,
  useApiClientsOrNull,
  usePropertyAccess,
} from 'design-system/my-lib';
import { Toast } from 'primereact/toast';
import { logAnalyticsEvent } from '@center-technologies/analytics';
import './GeneralSection.scss';

const GeneralSection: React.FC = () => {
  const { t } = useTranslation();
  const apiClients = useApiClientsOrNull();
  const toastRef = useRef<Toast>(null);

  const { orgId } = useParams<{ orgId: string }>();

  const [loadingData, setLoadingData] = useState(true);
  const [saving, setSaving] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [domain, setDomain] = useState('');

  // Fails closed: if no access prop declared, usePropertyAccess returns canWrite: true
  const nameAccess = usePropertyAccess(orgId, undefined);
  const descriptionAccess = usePropertyAccess(orgId, undefined);

  useEffect(() => {
    if (!apiClients?.graphClient || !orgId) return;
    let cancelled = false;
    setLoadingData(true);
    setFetchError(null);
    (async () => {
      try {
        const response = await apiClients.graphClient.get(`/spheres/${orgId}`);
        if (cancelled) return;
        const sphere = response.sphere ?? response;
        setName(sphere.name ?? '');
        setDescription(sphere.description ?? '');
        setDomain(sphere.domain ?? '');
      } catch {
        if (cancelled) return;
        setFetchError(t('orgSettings.general.errors.fetchFailed'));
      } finally {
        if (!cancelled) setLoadingData(false);
      }
    })();
    return () => { cancelled = true; };
  }, [apiClients, orgId, t]);

  const handleSave = useCallback(async () => {
    if (!apiClients?.graphClient || !orgId) return;
    setSaving(true);
    setSaveError(null);
    try {
      const fieldsChanged = ['name', 'description'].filter((f) =>
        f === 'name' ? name.trim() !== '' : description.trim() !== ''
      );
      await apiClients.graphClient.patch(`/spheres/${orgId}/properties`, {
        name: name.trim(),
        description: description.trim(),
      });
      logAnalyticsEvent('org_profile_saved', undefined, {
        orgId,
        fieldsChanged: fieldsChanged.join(','),
      });
      toastRef.current?.show({
        severity: 'success',
        summary: t('orgSettings.general.saveSuccess'),
        life: 3000,
      });
    } catch {
      setSaveError(t('orgSettings.general.errors.saveFailed'));
    } finally {
      setSaving(false);
    }
  }, [apiClients, orgId, name, description, t]);

  if (loadingData) {
    return (
      <div className="general-section__loading" role="status">
        <SpinnerAtom size="large" label={t('orgSettings.general.loading')} />
      </div>
    );
  }

  if (fetchError) {
    return (
      <div className="general-section__error" role="alert">
        <i className="pi pi-exclamation-triangle" aria-hidden="true" />
        <span>{fetchError}</span>
      </div>
    );
  }

  return (
    <div className="general-section">
      <Toast ref={toastRef} />

      <header className="general-section__header">
        <h2 className="general-section__title">{t('orgSettings.general.title')}</h2>
        <p className="general-section__subtitle">{t('orgSettings.general.subtitle')}</p>
      </header>

      <form
        className="general-section__form"
        onSubmit={(e) => { e.preventDefault(); handleSave(); }}
      >
        <div className="general-section__field">
          <label htmlFor="org-name" className="general-section__label">
            {t('orgSettings.general.fields.name')}
          </label>
          {nameAccess.loading ? (
            <SpinnerAtom size="small" label="" />
          ) : (
            <div className="general-section__input-row">
              <InputAtom
                id="org-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={!nameAccess.canWrite || saving}
                aria-label={t('orgSettings.general.fields.name')}
                className="general-section__input"
              />
              {!nameAccess.canWrite && (
                <span
                  className="general-section__access-lock"
                  title={t('orgSettings.general.accessDenied')}
                  aria-label={t('orgSettings.general.accessDenied')}
                >
                  <i className="pi pi-lock" aria-hidden="true" />
                </span>
              )}
            </div>
          )}
        </div>

        <div className="general-section__field">
          <label htmlFor="org-domain" className="general-section__label">
            {t('orgSettings.general.fields.domain')}
          </label>
          <span
            id="org-domain"
            className="general-section__readonly"
            aria-label={t('orgSettings.general.fields.domain')}
          >
            {domain || t('orgSettings.general.noDomain')}
          </span>
          <small className="general-section__hint">{t('orgSettings.general.domainHint')}</small>
        </div>

        <div className="general-section__field">
          <label htmlFor="org-description" className="general-section__label">
            {t('orgSettings.general.fields.description')}
          </label>
          {descriptionAccess.loading ? (
            <SpinnerAtom size="small" label="" />
          ) : (
            <div className="general-section__input-row">
              <TextareaAtom
                id="org-description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                disabled={!descriptionAccess.canWrite || saving}
                aria-label={t('orgSettings.general.fields.description')}
                className="general-section__textarea"
                rows={4}
                resize="vertical"
              />
              {!descriptionAccess.canWrite && (
                <span
                  className="general-section__access-lock"
                  title={t('orgSettings.general.accessDenied')}
                  aria-label={t('orgSettings.general.accessDenied')}
                >
                  <i className="pi pi-lock" aria-hidden="true" />
                </span>
              )}
            </div>
          )}
        </div>

        {saveError && (
          <div className="general-section__save-error" role="alert">
            {saveError}
          </div>
        )}

        <div className="general-section__actions">
          <PrimeReactButton
            type="submit"
            label={t('common.actions.save')}
            disabled={saving || nameAccess.loading || descriptionAccess.loading}
            loading={saving}
          />
        </div>
      </form>
    </div>
  );
};

export default GeneralSection;
